import React, { useEffect, useState } from "react";
import NavBar from "@/components/NavBar";
import api from "@/lib/api";
import { fmt } from "@/lib/format";
import { toast } from "sonner";

export default function DriverStats() {
  const [stats, setStats] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [mySectors, setMySectors] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, sec, cls, me] = await Promise.all([
          api.get("/driver/stats"),
          api.get("/sectors"),
          api.get("/sector-clusters"),
          api.get("/auth/me"),
        ]);
        setStats(s.data);
        setSectors(sec.data);
        setClusters(cls.data);
        setMySectors(me.data.sectors || []);
      } catch { toast.error("Échec du chargement"); }
    })();
  }, []);

  const toggleSector = (id) => {
    setMySectors((arr) => arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);
  };

  const applyCluster = (cluster) => {
    setMySectors(cluster.sectors);
    toast.success(`Cluster « ${cluster.name} » appliqué`);
  };

  const clearSectors = () => setMySectors([]);

  const saveSectors = async () => {
    try {
      await api.patch("/driver/sectors", { sectors: mySectors });
      toast.success("Secteurs enregistrés");
    } catch { toast.error("Échec"); }
  };

  if (!stats) return (
    <div className="min-h-screen bg-white">
      <NavBar />
      <main className="p-6"><p className="font-mono-eyebrow">// Chargement...</p></main>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <NavBar><span className="font-mono-eyebrow hidden md:inline">// Statistiques</span></NavBar>
      <main className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="driver-stats-page">
        <p className="font-mono-eyebrow">// Mes performances</p>
        <h1 className="font-display text-4xl mt-1">STATISTIQUES</h1>

        {stats.penalty_until && new Date(stats.penalty_until) > new Date() && (
          <div className="mt-6 brut-card p-4 bg-[#FFD600]" data-testid="penalty-alert">
            <p className="font-mono-eyebrow">// Suspension active</p>
            <p className="font-display text-xl mt-1">Compte suspendu jusqu'au {new Date(stats.penalty_until).toLocaleString("fr-CH")}</p>
            <p className="text-xs mt-1">Vous ne pouvez pas accepter de nouvelles courses pendant cette période.</p>
          </div>
        )}

        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
          {[
            ["Courses terminées", stats.completed_rides],
            ["Revenus totaux", fmt(stats.earnings_chf)],
            ["Annulations (30j)", `${stats.cancellations_30d} / ${stats.threshold}`],
            ["Fiabilité", `${stats.reliability_pct}%`],
          ].map(([k, v]) => (
            <div key={k} className="brut-card p-5">
              <p className="font-mono-eyebrow text-[#52525B]">{k}</p>
              <p className="font-display text-4xl mt-1">{v}</p>
            </div>
          ))}
        </div>

        <section className="mt-10">
          <p className="font-mono-eyebrow">// Secteurs</p>
          <h2 className="font-display text-2xl mt-1">MES ZONES DE TRAVAIL</h2>
          <p className="text-sm text-[#52525B] mt-2">Sélectionnez les secteurs où vous souhaitez recevoir des courses. Si aucun n'est sélectionné, vous recevez toutes les courses.</p>

          <p className="font-mono-eyebrow mt-5">// Clusters préréglés</p>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="clusters">
            {clusters.map(c => (
              <button
                key={c.id}
                data-testid={`cluster-${c.id}`}
                onClick={() => applyCluster(c)}
                className="border-2 border-black bg-white px-3 py-2 font-mono-eyebrow text-xs uppercase hover:bg-[#FFD600]"
              >{c.name} <span className="text-[#52525B]">({c.sectors.length})</span></button>
            ))}
            <button
              data-testid="cluster-clear"
              onClick={clearSectors}
              className="border-2 border-black bg-black text-white px-3 py-2 font-mono-eyebrow text-xs uppercase"
            >Tout effacer</button>
          </div>

          <p className="font-mono-eyebrow mt-5">// Sélection manuelle</p>
          <div className="mt-2 grid sm:grid-cols-3 gap-2">
            {sectors.map(s => (
              <button
                key={s.id}
                data-testid={`sector-${s.id}`}
                onClick={() => toggleSector(s.id)}
                className={`border-2 border-black p-3 text-left ${mySectors.includes(s.id) ? "bg-[#FFD600]" : "bg-white"}`}
              >
                <p className="font-display text-sm">{s.name}</p>
                <p className="font-mono-eyebrow text-[10px] mt-1 text-[#52525B]">{s.source === "official" ? "officiel · OSM" : "embedded"} · {s.polygon?.length || 0} pts</p>
              </button>
            ))}
          </div>
          <p className="text-xs mt-2 text-[#52525B]">Sélectionnés : {mySectors.length || "aucun"}</p>
          <button data-testid="save-sectors-btn" onClick={saveSectors} className="brut-btn mt-4">Enregistrer →</button>
        </section>

        <section className="mt-10">
          <p className="font-mono-eyebrow">// Revenus par secteur</p>
          <h2 className="font-display text-2xl mt-1">REVENUS PAR ZONE</h2>
          {!stats.revenue_by_sector || stats.revenue_by_sector.length === 0 ? (
            <p className="mt-3 text-sm text-[#52525B]">Aucune course terminée pour le moment.</p>
          ) : (
            <div className="mt-3 brut-card overflow-x-auto" data-testid="revenue-by-sector-table">
              <table className="w-full">
                <thead className="bg-black text-white">
                  <tr>{["Secteur", "Courses", "Revenus"].map(h => <th key={h} className="text-left p-3 font-mono-eyebrow">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {stats.revenue_by_sector.map(r => {
                    const maxRev = Math.max(...stats.revenue_by_sector.map(x => x.revenue_chf));
                    const pct = Math.round(100 * r.revenue_chf / maxRev);
                    return (
                      <tr key={r.sector_id} className="border-t-2 border-black">
                        <td className="p-3">
                          <p className="font-display">{r.sector_name}</p>
                          <div className="mt-1 h-2 bg-[#F4F4F5] border border-black">
                            <div className="h-full bg-[#FFD600]" style={{ width: `${pct}%` }} />
                          </div>
                        </td>
                        <td className="p-3">{r.rides}</td>
                        <td className="p-3 font-display">{fmt(r.revenue_chf)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="mt-10">
          <p className="font-mono-eyebrow">// Historique des annulations</p>
          <h2 className="font-display text-2xl mt-1">ANNULATIONS</h2>
          {stats.history.length === 0 ? (
            <p className="mt-3 text-sm text-[#52525B]">Aucune annulation. Continuez comme ça !</p>
          ) : (
            <div className="mt-3 brut-card overflow-x-auto" data-testid="cancellations-table">
              <table className="w-full">
                <thead className="bg-black text-white">
                  <tr>{["Date d'annulation", "Course prévue", "Préavis (h)"].map(h => <th key={h} className="text-left p-3 font-mono-eyebrow">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {stats.history.map(c => (
                    <tr key={c.cancel_id} className="border-t-2 border-black">
                      <td className="p-3">{new Date(c.released_at).toLocaleString("fr-CH")}</td>
                      <td className="p-3">{c.scheduled_for ? new Date(c.scheduled_for).toLocaleString("fr-CH") : "-"}</td>
                      <td className="p-3 font-display">{c.hours_before}h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
